import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import { states, cities } from './Doctorstatecity';
import { doctorSpecializations } from './doctorSpecializations';
import './DoctorProfile.css';

// MUI Imports
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Input,
} from '@mui/material';
import {
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';

const DoctorProfile = () => {
  const { doctorUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const daysOfWeek = useMemo(() => [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ], []);
  const timeSlots = useMemo(() => [
    '6:00 AM - 7:00 AM', '7:00 AM - 8:00 AM', '8:00 AM - 9:00 AM', '9:00 AM - 10:00 AM',
    '10:00 AM - 11:00 AM', '11:00 AM - 12:00 PM', '12:00 PM - 1:00 PM', '1:00 PM - 2:00 PM',
    '2:00 PM - 3:00 PM', '3:00 PM - 4:00 PM', '4:00 PM - 5:00 PM', '5:00 PM - 6:00 PM',
    '6:00 PM - 7:00 PM', '7:00 PM - 8:00 PM', '8:00 PM - 9:00 PM', '9:00 PM - 10:00 PM',
    '10:00 PM - 11:00 PM', '11:00 PM - 12:00 AM'
  ], []);

  const [doctor, setDoctor] = useState({
    name: '', email: '', phone: '', specialization: '', state: '', city: '',
    experience: '', address: '', consultationFee: '', availability: {},
    profilePicture: 'https://via.placeholder.com/150',
    bankDetails: {
      accountHolderName: '', bankName: '', branchName: '', accountNumber: '',
      confirmAccountNumber: '', ifscCode: '', upiId: '', paytmNumber: '',
      googlePayNumber: '', phonePeNumber: ''
    }
  });
  const [activeSection, setActiveSection] = useState('Personal Information');
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false); // New state for update processing
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [error, setError] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [newBankDetails, setNewBankDetails] = useState({
    accountHolderName: '', bankName: '', branchName: '', accountNumber: '',
    confirmAccountNumber: '', ifscCode: '', upiId: '', paytmNumber: '',
    googlePayNumber: '', phonePeNumber: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [profilePicFile, setProfilePicFile] = useState(null);

  const normalizeAvailability = useCallback((rawData = {}) => {
    const result = {};
    daysOfWeek.forEach(day => {
      result[day] = Array.isArray(rawData[day]) ? rawData[day].map(slot => slot.trim()) : [];
    });
    return result;
  }, [daysOfWeek]);

  const fetchDoctorDetails = useCallback(async () => {
    setIsFetching(true);
    if (!doctorUser || doctorUser.role !== 'doctor') {
      setError('You are not authorized to view this profile. Please log in as a doctor.');
      setIsFetching(false);
      return;
    }

    try {
      const [profileRes, bankRes] = await Promise.all([
        api.get('/doctors/profile', { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } }),
        api.get(`/doctors/bank-details/${doctorUser.id}`, { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } })
      ]);
      const data = profileRes.data;
      const normalizedAvailability = normalizeAvailability(data.availability);

      setDoctor({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        specialization: data.specialization || '',
        state: data.state || '',
        city: data.city || '',
        experience: data.experience?.toString() || '',
        address: data.address || '',
        consultationFee: data.consultationFee?.toString() || '',
        availability: normalizedAvailability,
        profilePicture: data.profilePicture || 'https://via.placeholder.com/150',
        bankDetails: bankRes.data || {
          accountHolderName: '', bankName: '', branchName: '', accountNumber: '',
          confirmAccountNumber: '', ifscCode: '', upiId: '', paytmNumber: '',
          googlePayNumber: '', phonePeNumber: ''
        }
      });
      setError('');
    } catch (error) {
      setError('Failed to fetch doctor details: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsFetching(false);
    }
  }, [doctorUser, normalizeAvailability]);

  useEffect(() => {
    fetchDoctorDetails();
  }, [fetchDoctorDetails]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDoctor(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicFile(file);
      setDoctor(prev => ({
        ...prev,
        profilePicture: URL.createObjectURL(file)
      }));
    }
  };

  const handleUpdateProfile = async () => {
    if (!doctorUser) {
      setError('Please log in to update your profile.');
      return;
    }
    setIsUpdating(true); // Start the updating animation
    try {
      const formData = new FormData();
      formData.append('name', doctor.name.trim());
      formData.append('email', doctor.email.trim());
      formData.append('phone', doctor.phone.trim());
      formData.append('specialization', doctor.specialization.trim());
      formData.append('state', doctor.state.trim());
      formData.append('city', doctor.city.trim());
      formData.append('experience', parseInt(doctor.experience) || 0);
      formData.append('address', doctor.address.trim());
      formData.append('consultationFee', parseInt(doctor.consultationFee) || 0);
      formData.append('availability', JSON.stringify(doctor.availability));
      if (profilePicFile) {
        formData.append('profilePicture', profilePicFile);
      }

      const response = await api.put('/doctors/profile', formData, {
        headers: { 
          Authorization: `Bearer ${doctorUser.accessToken}`,
          'Content-Type': 'multipart/form-data'
        },
      });

      const updatedData = response.data;
      console.log('API Response:', updatedData);

      setDoctor(prev => {
        const newState = {
          ...prev,
          ...updatedData,
          availability: normalizeAvailability(updatedData.availability || prev.availability),
          profilePicture: updatedData.profilePicture,
        };
        console.log('New Doctor State:', newState);
        return newState;
      });

      const img = new Image();
      img.src = updatedData.profilePicture;
      img.onload = () => console.log('Image loaded:', updatedData.profilePicture);
      img.onerror = () => console.error('Image failed to load:', updatedData.profilePicture);

      setIsEditing(false);
      setSuccessMessage('Profile updated successfully!');
      setProfilePicFile(null);
    } catch (error) {
      console.error('Update Error:', error.response?.data || error.message);
      setError('Failed to update profile: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsUpdating(false); // Stop the updating animation
    }
  };

  const handleAddSlots = () => {
    if (selectedDay && selectedSlots.length > 0 && daysOfWeek.includes(selectedDay)) {
      setDoctor(prev => ({
        ...prev,
        availability: { ...prev.availability, [selectedDay]: selectedSlots },
      }));
      setSelectedDay('');
      setSelectedSlots([]);
    }
  };

  const handleRemoveSlot = (day, slot) => {
    setDoctor(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: prev.availability[day].filter(s => s !== slot),
      },
    }));
  };

  const handleBankDetailsChange = (e) => {
    const { name, value } = e.target;
    setNewBankDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleBankDetailsSubmit = async () => {
    if (newBankDetails.accountNumber !== newBankDetails.confirmAccountNumber) {
      setError('Bank account numbers do not match.');
      return;
    }
    setIsUpdating(true); // Start the updating animation for bank details
    try {
      await api.put(`/doctors/bank-details/${doctorUser.id}`, newBankDetails, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      setBankDialogOpen(false);
      setSuccessMessage('Bank details updated successfully!');
      setNewBankDetails({
        accountHolderName: '', bankName: '', branchName: '', accountNumber: '',
        confirmAccountNumber: '', ifscCode: '', upiId: '', paytmNumber: '',
        googlePayNumber: '', phonePeNumber: ''
      });
      fetchDoctorDetails();
    } catch (error) {
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      setError(`Failed to update bank details: ${errorMessage}`);
    } finally {
      setIsUpdating(false); // Stop the updating animation
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async () => {
    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setIsUpdating(true); // Start the updating animation for password
    try {
      const response = await api.post(
        '/doctors/doctor/change-password',
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        },
        { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } }
      );
      setPasswordSuccess(response.data.message);
      setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
      setIsChangingPassword(false);
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Failed to change password.');
    } finally {
      setIsUpdating(false); // Stop the updating animation
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const sections = [
    { name: 'Personal Information', icon: <PersonIcon /> },
    { name: 'Availability', icon: <ScheduleIcon /> },
    { name: 'Bank Account Details', icon: <BankIcon /> },
  ];

  if (isFetching) {
    return (
      <Box className="dp-container">
        <Drawer
          variant="temporary"
          open={sidebarOpen}
          onClose={toggleSidebar}
          sx={{
            '& .MuiDrawer-paper': { width: 220, boxSizing: 'border-box', bgcolor: '#fff' },
          }}
        >
          <Box className="dp-sidebar-top">
            <img 
              src={`${doctor.profilePicture}?t=${new Date().getTime()}`} 
              alt="Profile" 
              className="dp-profile-img" 
              onError={(e) => {
                console.log('Image load error:', doctor.profilePicture);
                e.target.src = 'https://placehold.co/150x150';
              }}
            />
            <Typography variant="h6" className="dp-profile-name">{doctor.name}</Typography>
          </Box>
          <List>
            {sections.map(section => (
              <ListItem
                button
                key={section.name}
                selected={activeSection === section.name}
                onClick={() => {
                  setActiveSection(section.name);
                  setSidebarOpen(false);
                }}
                sx={{ '&.Mui-selected': { bgcolor: '#e3f2fd' } }}
              >
                <ListItemIcon>{section.icon}</ListItemIcon>
                <ListItemText primary={section.name} />
              </ListItem>
            ))}
          </List>
        </Drawer>
        <Box className="dp-content-wrapper">
          <Button className="sidebar-toggle" onClick={toggleSidebar}>
            {sidebarOpen ? "✕" : "☰"}
          </Button>
          <Box className="dp-loading"><CircularProgress /></Box>
        </Box>
      </Box>
    );
  }

  if (error && !isUpdating) { // Show error only if not updating
    return (
      <Box className="dp-container">
        <Box className="dp-content-wrapper">
          <Box className="dp-error">
            <Typography color="error">{error}</Typography>
            <Button variant="contained" color="primary" onClick={() => navigate('/doctor-login')} sx={{ mt: 2 }}>
              Go to Login
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="dp-container">
      {/* Sidebar */}
      <Drawer
        variant={sidebarOpen ? "temporary" : "permanent"}
        open={sidebarOpen}
        onClose={toggleSidebar}
        sx={{
          display: { xs: sidebarOpen ? 'block' : 'none', md: 'block' },
          '& .MuiDrawer-paper': { width: 220, boxSizing: 'border-box', bgcolor: '#fff' },
        }}
      >
        <Box className="dp-sidebar-top">
          <img 
            src={`${doctor.profilePicture}?t=${new Date().getTime()}`} 
            alt="Profile" 
            className="dp-profile-img" 
            key={doctor.profilePicture}
            onError={(e) => {
              console.log('Image load error:', doctor.profilePicture);
              e.target.src = 'https://placehold.co/150x150';
            }}
          />
          <Typography variant="h6" className="dp-profile-name">{doctor.name}</Typography>
        </Box>
        <List>
          {sections.map(section => (
            <ListItem
              button
              key={section.name}
              selected={activeSection === section.name}
              onClick={() => {
                setActiveSection(section.name);
                setSidebarOpen(false);
              }}
              sx={{ '&.Mui-selected': { bgcolor: '#e3f2fd' } }}
            >
              <ListItemIcon>{section.icon}</ListItemIcon>
              <ListItemText primary={section.name} />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box className="dp-content-wrapper">
        <Button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarOpen ? "✕" : "☰"}
        </Button>
        <Box className="dp-content">
          {isUpdating && (
            <Box className="dp-updating-overlay">
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>Updating Profile...</Typography>
            </Box>
          )}
          <Typography variant="h5" className="dp-section-header">{activeSection}</Typography>

          {/* Personal Information */}
          {activeSection === 'Personal Information' && (
            <Box className="dp-section">
               {!isEditing ? (
                <Box className="dp-view">
                  <img src={doctor.profilePicture} alt="Profile" className="dp-profile-pic-view" />
                  {['name', 'email', 'phone', 'specialization', 'state', 'city', 'experience', 'address', 'consultationFee'].map(field => (
                    <Box key={field} className="dp-info-row">
                      <Typography className="dp-info-key">{field.charAt(0).toUpperCase() + field.slice(1).replace(/Fee/, ' Fee (₹)')}:</Typography>
                      <Typography className="dp-info-val">{doctor[field] || 'Not Set'}</Typography>
                    </Box>
                  ))}
                  <Button variant="contained" color="primary" onClick={() => setIsEditing(true)} className="dp-update-btn" disabled={isUpdating}>
                    Update
                  </Button>
                  <Button variant="contained" color="secondary" onClick={() => setIsChangingPassword(true)} className="dp-update-btn" disabled={isUpdating}>
                    Change Password
                  </Button>
                </Box>
              ) : (
                <Box className="dp-edit">
                  <Box className="dp-profile-pic-edit">
                    <img src={doctor.profilePicture} alt="Profile Preview" className="dp-profile-pic-preview" />
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="dp-profile-pic-input"
                      disabled={isUpdating}
                    />
                  </Box>
                  <TextField
                    label="Name"
                    name="name"
                    value={doctor.name || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    className="dp-input"
                    disabled={isUpdating}
                  />
                  <TextField
                    label="Email"
                    name="email"
                    value={doctor.email || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    type="email"
                    variant="outlined"
                    className="dp-input"
                    disabled={isUpdating}
                  />
                  <TextField
                    label="Phone"
                    name="phone"
                    value={doctor.phone || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    type="tel"
                    required
                    variant="outlined"
                    className="dp-input"
                    disabled={isUpdating}
                  />
                  <Select
                    name="specialization"
                    value={doctor.specialization || ''}
                    onChange={handleChange}
                    fullWidth
                    displayEmpty
                    variant="outlined"
                    className="dp-input"
                    disabled={isUpdating}
                  >
                    <MenuItem value="">Select Specialization</MenuItem>
                    {doctorSpecializations.map(spec => (
                      <MenuItem key={spec} value={spec}>{spec}</MenuItem>
                    ))}
                  </Select>
                  <Select
                    name="state"
                    value={doctor.state || ''}
                    onChange={handleChange}
                    fullWidth
                    displayEmpty
                    variant="outlined"
                    className="dp-input"
                    disabled={isUpdating}
                  >
                    <MenuItem value="">Select State</MenuItem>
                    {states.map(state => (
                      <MenuItem key={state} value={state}>{state}</MenuItem>
                    ))}
                  </Select>
                  <Select
                    name="city"
                    value={doctor.city || ''}
                    onChange={handleChange}
                    fullWidth
                    displayEmpty
                    variant="outlined"
                    className="dp-input"
                    disabled={!doctor.state || isUpdating}
                  >
                    <MenuItem value="">Select City</MenuItem>
                    {doctor.state && cities[doctor.state]?.map(city => (
                      <MenuItem key={city} value={city}>{city}</MenuItem>
                    ))}
                  </Select>
                  <TextField
                    label="Experience (in years)"
                    name="experience"
                    value={doctor.experience || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    type="number"
                    variant="outlined"
                    className="dp-input"
                    disabled={isUpdating}
                  />
                  <TextField
                    label="Address"
                    name="address"
                    value={doctor.address || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    className="dp-input"
                    disabled={isUpdating}
                  />
                  <TextField
                    label="Consultation Fee (₹)"
                    name="consultationFee"
                    value={doctor.consultationFee || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    type="number"
                    variant="outlined"
                    className="dp-input"
                    disabled={isUpdating}
                  />
                  <Box className="dp-edit-controls">
                    <Button variant="contained" color="primary" onClick={handleUpdateProfile} disabled={isUpdating}>
                      {isUpdating ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => setIsEditing(false)} disabled={isUpdating}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
              {isChangingPassword && (
                <Box className="dp-change-password" sx={{ mt: 3 }}>
                  <Typography variant="h6">Change Password</Typography>
                  {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
                  {passwordSuccess && <Alert severity="success" sx={{ mb: 2 }}>{passwordSuccess}</Alert>}
                  <TextField
                    label="Current Password"
                    name="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    required
                    disabled={isUpdating}
                  />
                  <TextField
                    label="New Password"
                    name="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    required
                    disabled={isUpdating}
                  />
                  <TextField
                    label="Confirm New Password"
                    name="confirmNewPassword"
                    type="password"
                    value={passwordData.confirmNewPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    required
                    disabled={isUpdating}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button variant="contained" color="primary" onClick={handlePasswordSubmit} sx={{ mr: 1 }} disabled={isUpdating}>
                      {isUpdating ? 'Updating...' : 'Update Password'}
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => setIsChangingPassword(false)} disabled={isUpdating}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Availability */}
          {activeSection === 'Availability' && (
            <Box className="dp-section dp-availability">
              {!isEditing ? (
                <Box className="dp-view">
                  {Object.entries(doctor.availability).map(([day, slots]) => (
                    <Box key={day} className="dp-day">
                      <Typography className="dp-day-title">{day}</Typography>
                      {slots.length ? slots.map((slot, idx) => (
                        <Typography key={idx} className="dp-slot">{slot}</Typography>
                      )) : (
                        <Typography className="dp-no-slots">No slots set</Typography>
                      )}
                    </Box>
                  ))}
                  <Button variant="contained" color="primary" onClick={() => setIsEditing(true)} className="dp-update-btn" disabled={isUpdating}>
                    Update
                  </Button>
                </Box>
              ) : (
                <Box className="dp-edit">
                  <Select
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                    displayEmpty
                    fullWidth
                    variant="outlined"
                    className="dp-day-select"
                    disabled={isUpdating}
                  >
                    <MenuItem value="">Select a Day</MenuItem>
                    {daysOfWeek.map(day => <MenuItem key={day} value={day}>{day}</MenuItem>)}
                  </Select>
                  <Box className="dp-slot-grid">
                    {timeSlots.map(slot => (
                      <FormControlLabel
                        key={slot}
                        control={
                          <Checkbox
                            checked={selectedSlots.includes(slot)}
                            onChange={(e) => setSelectedSlots(prev =>
                              e.target.checked ? [...prev, slot] : prev.filter(s => s !== slot)
                            )}
                            disabled={isUpdating}
                          />
                        }
                        label={slot}
                        className="dp-slot-option"
                      />
                    ))}
                  </Box>
                  <Button variant="contained" color="primary" onClick={handleAddSlots} className="dp-add-slot" disabled={isUpdating}>
                    Add Slots
                  </Button>
                  <Box className="dp-slot-list">
                    {Object.entries(doctor.availability).map(([day, slots]) => (
                      <Box key={day} className="dp-day">
                        <Typography className="dp-day-title">{day}</Typography>
                        {slots.map((slot, idx) => (
                          <Box key={idx} className="dp-slot-row">
                            <Typography className="dp-slot">{slot}</Typography>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleRemoveSlot(day, slot)}
                              className="dp-remove-slot"
                              disabled={isUpdating}
                            >
                              Remove
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                  <Box className="dp-edit-controls">
                    <Button variant="contained" color="primary" onClick={handleUpdateProfile} disabled={isUpdating}>
                      {isUpdating ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => setIsEditing(false)} disabled={isUpdating}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Bank Account Details */}
          {activeSection === 'Bank Account Details' && (
            <Box className="dp-section">
              <Box className="dp-view">
                <Typography className="dp-bank-title">🏦 Doctor Bank Details</Typography>
                <Box className="dp-bank-subsection">
                  <Typography className="dp-subheader">1️⃣ Basic Bank Information</Typography>
                  <Box className="dp-info-row">
                    <Typography className="dp-info-key">Account Holder Name:</Typography>
                    <Typography className="dp-info-val">{doctor.bankDetails.accountHolderName || 'Not Set'}</Typography>
                  </Box>
                  <Box className="dp-info-row">
                    <Typography className="dp-info-key">Bank Name:</Typography>
                    <Typography className="dp-info-val">{doctor.bankDetails.bankName || 'Not Set'}</Typography>
                  </Box>
                  <Box className="dp-info-row">
                    <Typography className="dp-info-key">Branch Name:</Typography>
                    <Typography className="dp-info-val">{doctor.bankDetails.branchName || 'Not Set'}</Typography>
                  </Box>
                  <Box className="dp-info-row">
                    <Typography className="dp-info-key">Account Number:</Typography>
                    <Typography className="dp-info-val">{doctor.bankDetails.accountNumber || 'Not Set'}</Typography>
                  </Box>
                  <Box className="dp-info-row">
                    <Typography className="dp-info-key">IFSC Code:</Typography>
                    <Typography className="dp-info-val">{doctor.bankDetails.ifscCode || 'Not Set'}</Typography>
                  </Box>
                </Box>
                <Box className="dp-bank-subsection">
                  <Typography className="dp-subheader">2️⃣ UPI & Alternative Payment Methods</Typography>
                  <Box className="dp-info-row">
                    <Typography className="dp-info-key">UPI ID:</Typography>
                    <Typography className="dp-info-val">{doctor.bankDetails.upiId || 'Not Set'}</Typography>
                  </Box>
                  <Box className="dp-info-row">
                    <Typography className="dp-info-key">Paytm Number:</Typography>
                    <Typography className="dp-info-val">{doctor.bankDetails.paytmNumber || 'Not Set'}</Typography>
                  </Box>
                  <Box className="dp-info-row">
                    <Typography className="dp-info-key">Google Pay Number:</Typography>
                    <Typography className="dp-info-val">{doctor.bankDetails.googlePayNumber || 'Not Set'}</Typography>
                  </Box>
                  <Box className="dp-info-row">
                    <Typography className="dp-info-key">PhonePe Number:</Typography>
                    <Typography className="dp-info-val">{doctor.bankDetails.phonePeNumber || 'Not Set'}</Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setNewBankDetails(doctor.bankDetails);
                    setBankDialogOpen(true);
                  }}
                  className="dp-update-btn"
                  disabled={isUpdating}
                >
                  Update
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Bank Details Dialog */}
      <Dialog open={bankDialogOpen} onClose={() => setBankDialogOpen(false)} classes={{ paper: 'dp-dialog' }}>
        <DialogTitle className="dp-dialog-title">Update Bank Details</DialogTitle>
        <DialogContent>
          {isUpdating && (
            <Box className="dp-updating-overlay">
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>Updating Bank Details...</Typography>
            </Box>
          )}
          <Typography className="dp-dialog-subheader">1️⃣ Basic Bank Information</Typography>
          <TextField
            label="Account Holder Name"
            name="accountHolderName"
            value={newBankDetails.accountHolderName}
            onChange={handleBankDetailsChange}
            fullWidth
            margin="normal"
            variant="outlined"
            className="dp-dialog-field"
            disabled={isUpdating}
          />
          <TextField
            label="Bank Name"
            name="bankName"
            value={newBankDetails.bankName}
            onChange={handleBankDetailsChange}
            fullWidth
            margin="normal"
            variant="outlined"
            className="dp-dialog-field"
            disabled={isUpdating}
          />
          <TextField
            label="Branch Name"
            name="branchName"
            value={newBankDetails.branchName}
            onChange={handleBankDetailsChange}
            fullWidth
            margin="normal"
            variant="outlined"
            className="dp-dialog-field"
            disabled={isUpdating}
          />
          <TextField
            label="Account Number"
            name="accountNumber"
            value={newBankDetails.accountNumber}
            onChange={handleBankDetailsChange}
            fullWidth
            margin="normal"
            variant="outlined"
            className="dp-dialog-field"
            disabled={isUpdating}
          />
          <TextField
            label="Confirm Account Number"
            name="confirmAccountNumber"
            value={newBankDetails.confirmAccountNumber}
            onChange={handleBankDetailsChange}
            fullWidth
            margin="normal"
            variant="outlined"
            className="dp-dialog-field"
            disabled={isUpdating}
          />
          <TextField
            label="IFSC Code"
            name="ifscCode"
            value={newBankDetails.ifscCode}
            onChange={handleBankDetailsChange}
            fullWidth
            margin="normal"
            variant="outlined"
            className="dp-dialog-field"
            disabled={isUpdating}
          />
          <Typography className="dp-dialog-subheader">2️⃣ UPI & Alternative Payment Methods (Optional)</Typography>
          <TextField
            label="UPI ID"
            name="upiId"
            value={newBankDetails.upiId}
            onChange={handleBankDetailsChange}
            fullWidth
            margin="normal"
            variant="outlined"
            className="dp-dialog-field"
            disabled={isUpdating}
          />
          <TextField
            label="Paytm Number"
            name="paytmNumber"
            value={newBankDetails.paytmNumber}
            onChange={handleBankDetailsChange}
            fullWidth
            margin="normal"
            variant="outlined"
            className="dp-dialog-field"
            disabled={isUpdating}
          />
          <TextField
            label="Google Pay Number"
            name="googlePayNumber"
            value={newBankDetails.googlePayNumber}
            onChange={handleBankDetailsChange}
            fullWidth
            margin="normal"
            variant="outlined"
            className="dp-dialog-field"
            disabled={isUpdating}
          />
          <TextField
            label="PhonePe Number"
            name="phonePeNumber"
            value={newBankDetails.phonePeNumber}
            onChange={handleBankDetailsChange}
            fullWidth
            margin="normal"
            variant="outlined"
            className="dp-dialog-field"
            disabled={isUpdating}
          />
        </DialogContent>
        <DialogActions className="dp-dialog-actions">
          <Button variant="outlined" color="secondary" onClick={() => setBankDialogOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleBankDetailsSubmit} disabled={isUpdating}>
            {isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DoctorProfile;